/**
 * TitlePoint Transaction History
 *
 * Unified transaction history viewer that consolidates:
 * - All persistent transaction records (from database)
 * - Complete SOAP request/response audit trail
 * - Transaction metadata and status
 * - Search parameters and results
 *
 * This replaces separate session logs and persistent history displays
 */
import React, { useState, useEffect } from 'react';
import { useTitlePointHistory } from '../hooks/useTitlePointHistory';
import './TitlePointTransactionHistory.css';

function TitlePointTransactionHistory({ onBack }) {
  const {
    transactionHistory,
    loadingHistory,
    loadHistory,
    checkStatus
  } = useTitlePointHistory();

  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, processing, complete, failed
  const [searchQuery, setSearchQuery] = useState('');
  const [checkingStatus, setCheckingStatus] = useState({});
  const [modalContent, setModalContent] = useState(null);
  const [showPretty, setShowPretty] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleCheckStatus = async (requestId, transactionId) => {
    setCheckingStatus(prev => ({ ...prev, [transactionId]: true }));
    try {
      await checkStatus(requestId);
      // History will be auto-reloaded by the hook
    } catch (error) {
      console.error('Failed to check status:', error);
      alert(`Failed to check status: ${error.message}`);
    } finally {
      setCheckingStatus(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatXML = (xmlString) => {
    if (!xmlString || typeof xmlString !== 'string') return '';
    try {
      return xmlString
        .replace(/></g, '>\n<')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .join('\n');
    } catch (e) {
      return xmlString;
    }
  };

  const showModal = (title, content, isXML = false) => {
    setModalContent({ title, content, isXML });
    setShowPretty(true);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const downloadJSON = (data, filename) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter transactions
  const filteredTransactions = transactionHistory.filter(transaction => {
    // Status filter
    if (filterStatus !== 'all' && transaction.status !== filterStatus) {
      return false;
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const searchableText = [
        transaction.requestId,
        transaction.orderId,
        transaction.searchParams?.address,
        transaction.searchParams?.city,
        transaction.searchParams?.state,
        transaction.searchParams?.zip,
        transaction.workflowType
      ].filter(Boolean).join(' ').toLowerCase();

      if (!searchableText.includes(query)) {
        return false;
      }
    }

    return true;
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'complete':
        return 'status-badge-complete';
      case 'processing':
        return 'status-badge-processing';
      case 'pending':
        return 'status-badge-pending';
      case 'failed':
        return 'status-badge-failed';
      default:
        return 'status-badge-unknown';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="titlepoint-transaction-history">
      <div className="history-header">
        <div>
          <h2>TitlePoint Transaction History</h2>
          <p className="history-subtitle">
            Complete audit trail of all TitlePoint API requests and responses
          </p>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="history-controls">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search by address, request ID, order ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="status-filters">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-select"
          >
            <option value="all">All Statuses</option>
            <option value="complete">Complete</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <button
          className="refresh-btn"
          onClick={() => loadHistory()}
          disabled={loadingHistory}
        >
          {loadingHistory ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Transaction Count */}
      <div className="history-stats">
        <span className="stat-item">
          <strong>Total Transactions:</strong> {transactionHistory.length}
        </span>
        <span className="stat-item">
          <strong>Filtered:</strong> {filteredTransactions.length}
        </span>
      </div>

      {/* Loading State */}
      {loadingHistory && transactionHistory.length === 0 && (
        <div className="loading-message">
          Loading transaction history...
        </div>
      )}

      {/* Empty State */}
      {!loadingHistory && transactionHistory.length === 0 && (
        <div className="empty-state">
          <h3>No Transaction History</h3>
          <p>Run a TitlePoint workflow to create your first transaction record.</p>
        </div>
      )}

      {/* No Results */}
      {!loadingHistory && transactionHistory.length > 0 && filteredTransactions.length === 0 && (
        <div className="empty-state">
          <h3>No Matching Transactions</h3>
          <p>Try adjusting your filters or search query.</p>
        </div>
      )}

      {/* Transaction List */}
      {filteredTransactions.length > 0 && (
        <div className="transactions-list">
          {filteredTransactions.map((transaction) => {
            const isCheckingThisStatus = checkingStatus[transaction.transactionId];

            return (
              <div key={transaction.transactionId} className="transaction-card">
                {/* Transaction Header - Always Visible */}
                <div className="transaction-row">
                  <div className="transaction-info-grid">
                    <div className="info-cell">
                      <span className="info-label">Status</span>
                      <span className={`status-badge ${getStatusBadgeClass(transaction.status)}`}>
                        {getStatusLabel(transaction.status)}
                      </span>
                    </div>

                    <div className="info-cell">
                      <span className="info-label">Address</span>
                      <span className="info-value">
                        {transaction.searchParams?.address}, {transaction.searchParams?.city}, {transaction.searchParams?.state} {transaction.searchParams?.zip}
                      </span>
                    </div>

                    <div className="info-cell">
                      <span className="info-label">Request ID</span>
                      <span className="info-value">{transaction.requestId || 'N/A'}</span>
                    </div>

                    <div className="info-cell">
                      <span className="info-label">Order ID</span>
                      <span className="info-value">{transaction.orderId || 'N/A'}</span>
                    </div>

                    <div className="info-cell">
                      <span className="info-label">Submitted</span>
                      <span className="info-value">{formatTimestamp(transaction.timestamp)}</span>
                    </div>

                    <div className="info-cell">
                      <span className="info-label">Workflow</span>
                      <span className="info-value workflow-badge">
                        {transaction.workflowType?.replace(/_/g, ' ')}
                      </span>
                    </div>

                    {transaction.error && (
                      <div className="info-cell error-cell">
                        <span className="info-label">Error</span>
                        <span className="info-value error-text">{transaction.error}</span>
                      </div>
                    )}

                    {transaction.resultIds && transaction.resultIds.length > 0 && (
                      <div className="info-cell">
                        <span className="info-label">Result IDs</span>
                        <span className="info-value">{transaction.resultIds.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="transaction-actions-row">
                    {transaction.requestId && (
                      <button
                        className="action-btn check-status-btn"
                        onClick={() => handleCheckStatus(transaction.requestId, transaction.transactionId)}
                        disabled={isCheckingThisStatus}
                      >
                        {isCheckingThisStatus ? 'Checking...' : 'Check Status'}
                      </button>
                    )}

                    {transaction.createResponse && (
                      <button
                        className="action-btn"
                        onClick={() => showModal(
                          'CreateService3 Response',
                          transaction.createResponse.rawResponse || JSON.stringify(transaction.createResponse, null, 2),
                          !!transaction.createResponse.rawResponse
                        )}
                      >
                        CreateService3 Response
                      </button>
                    )}

                    {transaction.statusResponse && (
                      <button
                        className="action-btn"
                        onClick={() => showModal(
                          'GetRequestSummaries Response',
                          JSON.stringify(transaction.statusResponse, null, 2)
                        )}
                      >
                        GetRequestSummaries Response
                      </button>
                    )}

                    {transaction.lastStatusCheck && (
                      <button
                        className="action-btn"
                        onClick={() => showModal(
                          'Last Status Check (XML Response)',
                          transaction.lastStatusCheck,
                          true
                        )}
                      >
                        Status Check XML Response
                      </button>
                    )}

                    {transaction.resultResponses && transaction.resultResponses.length > 0 && (
                      <button
                        className="action-btn"
                        onClick={() => showModal(
                          `GetResultById Response${transaction.resultResponses.length > 1 ? 's' : ''}`,
                          JSON.stringify(transaction.resultResponses, null, 2)
                        )}
                      >
                        GetResultById Response ({transaction.resultResponses.length})
                      </button>
                    )}

                    <button
                      className="action-btn secondary-btn"
                      onClick={() => showModal(
                        'Complete Transaction Data',
                        JSON.stringify(transaction, null, 2)
                      )}
                    >
                      All Transaction Data
                    </button>

                    <button
                      className="action-btn secondary-btn"
                      onClick={() => downloadJSON(transaction, `titlepoint-${transaction.requestId}-${Date.now()}.json`)}
                    >
                      Download JSON
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalContent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalContent.title}</h3>
              <div className="modal-header-buttons">
                {modalContent.isXML && (
                  <button 
                    className="modal-toggle-btn" 
                    onClick={() => setShowPretty(!showPretty)}
                    title={showPretty ? 'Show raw XML' : 'Show formatted XML'}
                  >
                    {showPretty ? 'Raw' : 'Pretty'}
                  </button>
                )}
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>
            </div>
            <div className="modal-body">
              <pre className={modalContent.isXML ? 'xml-content' : 'json-content'}>
                {modalContent.isXML 
                  ? (showPretty ? formatXML(modalContent.content) : modalContent.content)
                  : modalContent.content
                }
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="history-help">
        <h3>About Transaction History</h3>
        <p>
          This view shows a complete audit trail of all TitlePoint API interactions. Each transaction displays:
        </p>
        <ul>
          <li><strong>Status & Metadata:</strong> Current status, timestamps, IDs, and search parameters</li>
          <li><strong>Check Status Button:</strong> For pending/processing transactions, click "Check Status" to call GetRequestSummaries and poll for completion. TitlePoint searches are asynchronous and may take seconds or minutes to complete.</li>
          <li><strong>Response Buttons:</strong> View CreateService3, GetRequestSummaries, and GetResultById responses in modal windows</li>
          <li><strong>Error Information:</strong> Any errors are displayed inline for quick diagnosis</li>
          <li><strong>Download:</strong> Export complete transaction data as JSON for archiving</li>
        </ul>
        <p className="help-note">
          <strong>Note:</strong> TitlePoint CreateService3 calls are asynchronous. Use "Check Status" to repeatedly poll GetRequestSummaries until the search completes.
          If CreateService3 fails (e.g., invalid county), the Request ID will be 0 and cannot be polled.
        </p>
      </div>
    </div>
  );
}

export default TitlePointTransactionHistory;
